import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { servicePages, servicePageBySlug } from "../data/servicePages";
import { serviceAreas } from "../data/serviceAreas";
import { SEO } from "./seo";

const SITE_URL = "https://clintonncpressurewashing.com";
const buildServiceSchemas = (service) => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${service.name} in Clinton, NC`,
    serviceType: service.name,
    description: service.metaDescription,
    url: `${SITE_URL}/services/${service.slug}`,
    areaServed: [
      {
        "@type": "City",
        name: "Clinton, NC",
      },
      {
        "@type": "AdministrativeArea",
        name: "Sampson County, NC",
      },
    ],
    provider: {
      "@type": "HomeAndConstructionBusiness",
      name: "Hamilton Pressure Washing",
      url: SITE_URL,
      telephone: "+1-910-990-1974",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: service.faq.map((entry) => ({
      "@type": "Question",
      name: entry.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: entry.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: SITE_URL,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Services",
        item: `${SITE_URL}/services`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: service.name,
        item: `${SITE_URL}/services/${service.slug}`,
      },
    ],
  };

  return [serviceSchema, faqSchema, breadcrumbSchema];
};

export const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = slug ? servicePageBySlug[slug] : null;

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const relatedServices = servicePages
    .filter((entry) => entry.slug !== service.slug)
    .slice(0, 3);
  const featuredAreas = serviceAreas.slice(0, 4);

  return (
    <>
      <SEO
        title={service.metaTitle}
        description={service.metaDescription}
        schemaData={buildServiceSchemas(service)}
      />

      <main id="service-detail-page">
        <section className="service-detail-hero">
          <div className="container">
            <p className="service-detail-eyebrow">Exterior Cleaning In Clinton, NC</p>
            <h1>{service.heroTitle}</h1>
            <p className="service-detail-intro">{service.intro}</p>
            <div className="service-detail-actions">
              <Link to="/contact" className="btn btn-custom btn-lg">
                Get a free quote
              </Link>
              <a href="tel:+19109901974" className="btn btn-custom btn-lg service-detail-call">
                Call (910) 990-1974
              </a>
            </div>
          </div>
        </section>

        <section className="service-detail-content">
          <div className="container">
            <figure className="service-detail-image">
              <img
                src={service.image || "/img/portfolio/5-Large.webp"}
                alt={
                  service.imageAlt ||
                  `${service.name} project by Hamilton Pressure Washing in Clinton NC`
                }
                loading="lazy"
                decoding="async"
              />
            </figure>
            <div className="row">
              <div className="col-md-8">
                {service.sections.map((section) => (
                  <article key={section.heading} className="service-detail-section">
                    <h2>{section.heading}</h2>
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </article>
                ))}

                <div className="service-detail-faq">
                  <h2>Frequently Asked Questions</h2>
                  {service.faq.map((entry) => (
                    <article key={entry.question} className="service-detail-faq-item">
                      <h3>{entry.question}</h3>
                      <p>{entry.answer}</p>
                    </article>
                  ))}
                </div>
              </div>

              <div className="col-md-4">
                <aside className="service-detail-sidebar">
                  <h3>What Is Included</h3>
                  <ul>
                    {service.includes.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>

                  <div className="service-detail-links">
                    <h4>Related Services</h4>
                    <ul>
                      {relatedServices.map((entry) => (
                        <li key={entry.slug}>
                          <Link to={`/services/${entry.slug}`}>
                            {entry.name.toLowerCase()} in Clinton, NC
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="service-detail-sidebar-cta">
                    <p>
                      Need professional {service.name.toLowerCase()} services in Clinton, NC or
                      nearby areas?
                    </p>
                    <Link to="/contact" className="btn btn-custom">
                      Request Quote
                    </Link>
                  </div>
                </aside>
              </div>
            </div>

            <div className="service-area-chips">
              <h3>Serving Clinton And Nearby Areas</h3>
              <div className="service-area-chip-list">
                {featuredAreas.map((area) => (
                  <Link key={area.slug} to={`/service-areas/${area.slug}`} className="service-area-chip">
                    {area.city}, {area.state}
                  </Link>
                ))}
              </div>
            </div>

            <div className="service-detail-related">
              <h3>More Exterior Cleaning Services</h3>
              <div className="row">
                {relatedServices.map((entry) => (
                  <div key={entry.slug} className="col-sm-4">
                    <Link to={`/services/${entry.slug}`} className="service-detail-related-card">
                      <h4>{entry.name}</h4>
                      <p>{entry.metaDescription}</p>
                      <span>Learn More</span>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};
